import type { LeadChannel, LeadStatus } from '@/types/api'

/**
 * How a lead's wait reads in the inbox.
 *
 * <p>
 * `late` is the same question the API's speed-to-lead reminder asks - New, nothing logged,
 * older than the threshold - so the row turns red when the reminder goes out and not at
 * some other moment of the browser's choosing. `soon` is half way there, which is when a rep
 * can still answer before anybody is told they did not.
 * </p>
 *
 * Null for a lead that is not waiting at all: once somebody has answered, the clock is not
 * the interesting thing about it.
 */
export type WaitTone = 'ok' | 'soon' | 'late'

export function waitTone(
  lead: { status: LeadStatus; createdAt: string; firstRespondedAt: string | null },
  thresholdMinutes: number,
  now: Date = new Date(),
): WaitTone | null {
  if (lead.status !== 'New' || lead.firstRespondedAt !== null) return null

  const created = new Date(lead.createdAt).getTime()
  if (Number.isNaN(created)) return null

  const minutes = (now.getTime() - created) / 60_000
  const threshold = Math.max(1, thresholdMinutes)

  if (minutes >= threshold) return 'late'
  if (minutes >= threshold / 2) return 'soon'

  return 'ok'
}

/** "4 min", "2 h 10 min", "3 days" - a wait as somebody says it out loud. */
export function waitLabel(createdAt: string, now: Date = new Date()): string {
  const created = new Date(createdAt).getTime()
  if (Number.isNaN(created)) return '-'

  const minutes = Math.max(0, Math.floor((now.getTime() - created) / 60_000))

  if (minutes < 60) return `${minutes} min`

  const hours = Math.floor(minutes / 60)
  if (hours < 48) {
    const rest = minutes % 60
    return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`
  }

  return `${Math.floor(hours / 24)} days`
}

export const CHANNEL_LABELS: Record<LeadChannel, string> = {
  Manual: 'Manual',
  WebForm: 'Web',
  MetaAds: 'Meta',
  GoogleAds: 'Google',
}

/** Open is everything somebody is still working; the rest have reached an answer. */
export function isOpenLead(status: LeadStatus): boolean {
  return status === 'New' || status === 'Working' || status === 'Qualified'
}

export function leadStatusTone(status: LeadStatus): 'neutral' | 'positive' | 'warning' | 'critical' | 'accent' {
  switch (status) {
    case 'New':
      return 'accent'
    case 'Qualified':
      return 'warning'
    case 'Converted':
      return 'positive'
    case 'Disqualified':
    case 'Junk':
      return 'neutral'
    default:
      return 'neutral'
  }
}
